import React from 'react';
import { Avatar, type AvatarProps } from '@mui/material';
import { useAuthenticatedObjectUrl } from './useAuthenticatedObjectUrl';
import {
  isBrowserNativeAvatarSrc,
  resolveAvatarImgSrc,
} from './resolveAvatarImgSrc';

export interface AuthAvatarProps extends Omit<AvatarProps, 'src'> {
  /** Relative `/api` path, signed avatar URL, or null for initials/fallback. */
  apiSrc?: string | null;
  /** Public static image when there is no authenticated avatar (e.g. gender default). */
  fallbackSrc?: string;
}

/**
 * MUI Avatar that loads signed avatar URLs as native images (HTTP cache)
 * and falls back to an authenticated blob fetch for remaining private paths.
 */
const AuthAvatar: React.FC<AuthAvatarProps> = ({
  apiSrc,
  fallbackSrc,
  children,
  ...rest
}) => {
  const needsAuthFetch = typeof apiSrc === 'string' && !isBrowserNativeAvatarSrc(apiSrc);
  const objectUrl = useAuthenticatedObjectUrl(needsAuthFetch ? apiSrc : null);
  const src = resolveAvatarImgSrc(apiSrc, { objectUrl, fallbackSrc });

  return (
    <Avatar key={apiSrc ?? 'no-avatar'} src={src} {...rest}>
      {children}
    </Avatar>
  );
};

export default AuthAvatar;
